import React from 'react';
import { useQuery } from '@apollo/client';
import { GET_HOURLY_EMPLOYEES } from '../../graphql/presetExpensesAndHourlyEmployees';
import ScrollIndicator from '../ScrollIndicator';

const HourlyEmployeesSection = ({ hourlyEmployeeHours = [], onHourlyEmployeeHoursChange, onHourlyEmployeeHoursBlur, isEditing = false }) => {
  // Fetch hourly employees from database for editing mode
  // For view mode (public or authenticated), use the saved hourlyEmployeeHours prop
  const { data, loading } = useQuery(GET_HOURLY_EMPLOYEES, {
    skip: !isEditing // Only fetch when editing
  });
  const hourlyEmployees = data?.hourlyEmployees || [];

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || amount === '') return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(Number(amount));
  };

  const handleHoursChange = (employeeId, hours) => {
    // Find the employee to get their hourly rate and contractor status
    const employee = hourlyEmployees.find(emp => emp.id === employeeId);
    
    const updatedHours = hourlyEmployeeHours.map(item => 
      item.employeeId === employeeId 
        ? { ...item, hours: hours, hourlyRate: employee?.hourlyRate, isContractor: employee?.isContractor }
        : item
    );
    
    // If employee not found in the hours array, add them with hourly rate and contractor status
    if (!hourlyEmployeeHours.find(item => item.employeeId === employeeId)) {
      updatedHours.push({ 
        employeeId, 
        hours,
        hourlyRate: employee?.hourlyRate || 0,
        isContractor: employee?.isContractor || false,
        name: employee?.name,
        job: employee?.job
      });
    }
    
    onHourlyEmployeeHoursChange(updatedHours);
  };

  const getEmployeeHours = (employeeId) => {
    const employeeRecord = hourlyEmployeeHours.find(item => item.employeeId === employeeId);
    return employeeRecord ? employeeRecord.hours : '';
  };

  const calculateEmployeePay = (employee, hours) => {
    if (!hours || isNaN(hours)) return 0;
    return employee.hourlyRate * parseFloat(hours);
  };

  const getTotalPay = () => {
    return hourlyEmployees.reduce((total, employee) => {
      const hours = getEmployeeHours(employee.id);
      return total + calculateEmployeePay(employee, hours);
    }, 0);
  };

  // Only show loading when editing (when we need to fetch from DB)
  if (loading && isEditing) {
    return (
      <div className="p-6">
        <div className="text-center py-8">
          <p className="text-gray-500">Loading hourly employees...</p>
        </div>
      </div>
    );
  }

  // Check if we have any data to display
  const hasDataToDisplay = isEditing 
    ? hourlyEmployees.length > 0 
    : hourlyEmployeeHours.some(emp => emp.hours && parseFloat(emp.hours) > 0);

  if (!hasDataToDisplay) {
    return (
      <div className="px-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Hourly Employees</h3>
        <div className="text-center py-8 bg-gray-50 rounded-lg">
          <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          <h3 className="text-sm font-medium text-gray-900 mb-1">No hourly employees</h3>
          <p className="text-sm text-gray-500">
            {isEditing 
              ? "Add hourly employees in the Preset Expenses & Employees page to track their hours here."
              : "No hourly employees were recorded for this report period."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="lg:p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900 px-2 lg:px-0">Hourly Employees</h3>
        
      </div>

      {isEditing && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
          <p className="text-sm text-blue-800">
            <svg className="inline w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Enter the number of hours worked for each employee. Pay is calculated automatically based on their hourly rate.
          </p>
        </div>
      )}

      <ScrollIndicator>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Job
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Type
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Hourly Rate
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Hours
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Total Pay
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {isEditing ? (
              // When editing, show all employees so users can enter hours
              hourlyEmployees.map((employee, index) => {
                const hours = getEmployeeHours(employee.id);
                const totalPay = calculateEmployeePay(employee, hours);
                
                return (
                  <tr key={employee.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {employee.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {employee.job}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                      {employee.isContractor ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                          Contractor
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                          Employee
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                      {formatCurrency(employee.hourlyRate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                      <input
                        type="number"
                        step="0.25"
                        min="0"
                        max="168" // Max hours in a week
                        value={hours}
                        onChange={(e) => handleHoursChange(employee.id, e.target.value)}
                        onBlur={onHourlyEmployeeHoursBlur}
                        className="w-20 px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500 text-right"
                        placeholder="0"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                      {formatCurrency(totalPay)}
                    </td>
                  </tr>
                );
              })
            ) : (
              // When viewing, use the saved hourlyEmployeeHours data directly
              hourlyEmployeeHours
                .filter(emp => emp.hours && parseFloat(emp.hours) > 0)
                .map((employee, index) => {
                  const hours = parseFloat(employee.hours);
                  const totalPay = employee.hourlyRate * hours;
                  
                  return (
                    <tr key={employee.employeeId || index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {employee.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {employee.job}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                        {employee.isContractor ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                            Contractor
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                            Employee
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                        {formatCurrency(employee.hourlyRate)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                        {hours} hours
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                        <span className="font-medium">{formatCurrency(totalPay)}</span>
                      </td>
                    </tr>
                  );
                })
            )}
          </tbody>
          {hourlyEmployeeHours.some(emp => emp.hours && parseFloat(emp.hours) > 0) && (
            <tfoot className="bg-blue-50 border-t-2 border-gray-300">
              <tr>
                <td colSpan="5" className="px-6 py-4 text-sm font-bold text-gray-900">
                  Total Hourly Employee Pay:
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900 text-right">
                  {formatCurrency(hourlyEmployeeHours.reduce((total, emp) => {
                    const hours = parseFloat(emp.hours) || 0;
                    return total + (emp.hourlyRate * hours);
                  }, 0))}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </ScrollIndicator>
    </div>
  );
};

export default HourlyEmployeesSection;
